import React, { useRef, useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import html2canvas from 'html2canvas';
import useUserStore from '../../stores/useUserStore';
import { useTranslation } from '../../hooks/useTranslation';
import useBrand from '../../hooks/useBrand';

interface ChartDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  chartRef: React.RefObject<HTMLDivElement>;
  title: string;
  subtitle?: string;
  /** Extra Modal classNames, e.g. z-index overrides when opening from
   *  high-z surfaces like the VictorIA widget. */
  classNames?: { wrapper?: string; backdrop?: string };
}

const ChartDownloadModal: React.FC<ChartDownloadModalProps> = ({
  isOpen,
  onClose,
  chartRef,
  title,
  subtitle,
  classNames,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { darkMode } = useUserStore();
  const { t } = useTranslation('common');
  const { assets: brandAssets } = useBrand();

  const bgColor = darkMode ? '#18181b' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#18181b';
  const subtitleColor = darkMode ? '#a1a1aa' : '#71717a';
  const poweredByColor = darkMode ? '#71717a' : '#a1a1aa';
  const logoSrc = darkMode ? brandAssets.fullLogoDark : brandAssets.fullLogoLight;

  // Load logo image for canvas drawing
  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  useEffect(() => {
    if (isOpen && chartRef.current) {
      setIsCapturing(true);
      const timer = setTimeout(async () => {
        try {
          const canvas = await html2canvas(chartRef.current!, {
            backgroundColor: bgColor,
            scale: 2,
            useCORS: true,
            logging: false,
          });
          setPreviewImage(canvas.toDataURL('image/png'));
        } catch (err) {
          console.error('Error capturing chart:', err);
        } finally {
          setIsCapturing(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      setPreviewImage(null);
    }
  }, [isOpen, chartRef, bgColor]);

  const handleDownload = async () => {
    if (!previewImage) return;
    setIsDownloading(true);
    try {
      // Load chart image and logo
      const [chartImg, logoImg] = await Promise.all([
        loadImage(previewImage),
        loadImage(logoSrc),
      ]);

      const scale = 2;
      const padding = 40 * scale;
      const titleHeight = 30 * scale;
      const subtitleHeight = subtitle ? 20 * scale : 0;
      const titlePadding = 16 * scale;
      const footerHeight = 50 * scale;
      const logoHeight = 18 * scale;
      const logoWidth = (logoImg.width / logoImg.height) * logoHeight;

      const canvasWidth = chartImg.width + padding * 2;
      const canvasHeight =
        padding + titleHeight + subtitleHeight + titlePadding + chartImg.height + footerHeight + padding;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d')!;

      // Background
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Title
      ctx.fillStyle = textColor;
      ctx.font = `bold ${18 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillText(title, padding, padding);

      // Subtitle
      if (subtitle) {
        ctx.fillStyle = subtitleColor;
        ctx.font = `${14 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillText(subtitle, padding, padding + titleHeight);
      }

      // Chart image
      const chartY = padding + titleHeight + subtitleHeight + titlePadding;
      ctx.drawImage(chartImg, padding, chartY, chartImg.width, chartImg.height);

      // Footer: "Powered by" + logo, bottom-right
      const footerY = chartY + chartImg.height + footerHeight / 2;
      const poweredByText = 'Powered by';
      ctx.fillStyle = poweredByColor;
      ctx.font = `${13 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.textBaseline = 'middle';
      const textWidth = ctx.measureText(poweredByText).width;
      const gap = 8 * scale;
      const totalWidth = textWidth + gap + logoWidth;
      const startX = canvasWidth - padding - totalWidth;

      ctx.fillText(poweredByText, startX, footerY);
      ctx.drawImage(
        logoImg,
        startX + textWidth + gap,
        footerY - logoHeight / 2,
        logoWidth,
        logoHeight
      );

      // Download
      const link = document.createElement('a');
      const brandSlug = brandAssets.shortName.toLowerCase().replace(/\s+/g, '');
      link.download = `${title.replace(/\s+/g, '_')}_${brandSlug}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading chart:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='5xl'
      scrollBehavior='inside'
      backdrop='blur'
      classNames={{
        wrapper: `items-center ${classNames?.wrapper ?? ''}`,
        backdrop: `bg-black/50 backdrop-blur-sm ${classNames?.backdrop ?? ''}`,
      }}
    >
      <ModalContent
        style={{ backgroundColor: bgColor }}
      >
        <ModalHeader style={{ color: textColor, backgroundColor: bgColor }}>
          {t('Vista previa')}
        </ModalHeader>
        <ModalBody className='py-6'>
          {isCapturing && (
            <div className='flex flex-col items-center justify-center h-64 gap-3'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500' />
              <span style={{ color: subtitleColor, fontSize: '14px' }}>
                {t('Cargando gráfico...')}
              </span>
            </div>
          )}
          {previewImage && !isCapturing && (
            <div
              ref={contentRef}
              className='rounded-xl overflow-hidden'
              style={{ backgroundColor: bgColor, padding: '16px' }}
            >
              {/* Title section */}
              <div style={{ padding: '20px 24px 8px' }}>
                <h3
                  style={{
                    color: textColor,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    margin: 0,
                  }}
                >
                  {title}
                </h3>
                {subtitle && (
                  <p
                    style={{
                      color: subtitleColor,
                      fontSize: '14px',
                      margin: '4px 0 0',
                    }}
                  >
                    {subtitle}
                  </p>
                )}
              </div>

              {/* Chart image */}
              <div style={{ padding: '0 16px 8px' }}>
                <img
                  src={previewImage}
                  alt={title}
                  style={{ width: '100%', borderRadius: '4px' }}
                />
              </div>

            </div>
          )}
        </ModalBody>
        <ModalFooter className='flex-col sm:flex-row gap-3 !justify-between' style={{ backgroundColor: bgColor }}>
          <div className='flex items-center gap-2 flex-1'>
            <span style={{ color: poweredByColor, fontSize: '13px' }}>
              Powered by
            </span>
            <img
              src={logoSrc}
              alt={brandAssets.name}
              style={{ height: '18px' }}
            />
          </div>
          <div className='flex gap-2'>
            <Button
              variant='light'
              onPress={onClose}
              style={{ color: subtitleColor }}
            >
              {t('Cerrar')}
            </Button>
            <Button
              color='primary'
              onPress={handleDownload}
              isLoading={isDownloading}
              isDisabled={!previewImage || isCapturing}
              startContent={
                !isDownloading && (
                  <Icon icon='mdi:download' className='text-lg' />
                )
              }
            >
              {t('Descargar imagen')}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChartDownloadModal;
